import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, Box, Typography, TextField, LinearProgress } from '@mui/material';
import moment from 'moment';

import { fetchCostReport } from '../../services/api';


const CostDashboardWidget = () => {
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadReport = useCallback(async (targetYear) => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetchCostReport({ year: targetYear });
            if (response?.report) {
                setReport(response.report);
            }
        } catch (e) {
            setError(e.message || 'Erreur de chargement du rapport');
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadReport(year);
    }, [year, loadReport]);

    const handleYearChange = (event) => {
        const val = Number(event.target.value);
        if (!Number.isNaN(val)) {
            setYear(val);
        }
    };

    const formatCurrency = (value, currency = 'EUR') => (
        new Intl.NumberFormat('fr-BE', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(value || 0)
    );

    const formatDate = (value) => (
        moment(value).format('DD/MM/YYYY')
    );

    return (
        <Box mt={2}>
            <Card>
                <CardContent>
                    <Box display="flex" alignItems="center" mb={2} gap={2}>
                        <Typography variant="h6">Rapport des coûts</Typography>
                        <Box flexGrow={1} />
                        <TextField
                            label="Année"
                            type="number"
                            size="small"
                            value={year}
                            onChange={handleYearChange}
                            sx={{ width: 120 }}
                        />
                    </Box>

                    {loading && <LinearProgress />}

                    {error && (
                        <Typography color="error" variant="body2" mb={2}>
                            {error}
                        </Typography>
                    )}

                    {report && !loading && (
                        <Box display="flex" flexWrap="wrap" gap={3}>
                            <Box>
                                <Typography variant="subtitle2">
                                    Passé ({formatDate(report.from_date)} → {formatDate(report.day)})
                                </Typography>
                                <Typography variant="h6">
                                    {formatCurrency(report.past.total)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Fourchette : {formatCurrency(report.past.total_min)} –{' '}
                                    {formatCurrency(report.past.total_max)}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2">
                                    Futur ({formatDate(report.day)} → {formatDate(report.year_end)})
                                </Typography>
                                <Typography variant="h6">
                                    {formatCurrency(report.future.total)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Fourchette : {formatCurrency(report.future.total_min)} –{' '}
                                    {formatCurrency(report.future.total_max)}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2">
                                    Total annuel estimé ({report.year})
                                </Typography>
                                <Typography variant="h6">
                                    {formatCurrency(report.past.total + report.future.total)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Fourchette :{' '}
                                    {formatCurrency(report.past.total_min + report.future.total_min)} –{' '}
                                    {formatCurrency(report.past.total_max + report.future.total_max)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default CostDashboardWidget;
